// MarcusAI Workbench API client (Capability 3, MW-4). Thin typed wrappers over
// the shared axios instance (`@/services/api`), matching the VB convention: auth
// is passed PER REQUEST (JWT header), not relied on from the global default, to
// avoid the cold-load 401 race. The X-Workspace-ID header is injected by the
// axios request interceptor for the read/write calls.
//
// The draft (MW-3) is a Server-Sent Events stream, which axios can't surface
// incrementally in the browser, so it uses `fetch` + a ReadableStream reader.
// That path bypasses the axios interceptor, so the workspace header is passed
// explicitly by the caller (from WorkspaceContext).

import api from "@/services/api";
import type {
  DraftFrame,
  DraftStreamHandlers,
  WorkbenchQueue,
  WorkbenchTaskPatch,
  WorkbenchTaskUpdate,
} from "@/types/workbench";

/** Per-request auth header (JWT), matching the app-wide convention. */
function authConfig(token: string | null | undefined) {
  return token ? { headers: { Authorization: `JWT ${token}` } } : undefined;
}

/** GET /api/workbench/companies/<company_id>/ — the whole fix queue for a
 *  company's latest paid report (progress + fixes[]). Paid-only, workspace-scoped
 *  on the backend. */
async function getQueue(companyId: number, token: string | null): Promise<WorkbenchQueue> {
  const { data } = await api.get<WorkbenchQueue>(
    `/workbench/companies/${companyId}/`,
    authConfig(token),
  );
  return data;
}

/** PATCH /api/workbench/companies/<company_id>/signals/<signal_key>/ — partial
 *  update of a fix's task state (status / notes / per-step ticks). `step_state`
 *  merges server-side, so send only the toggled indices. Returns the merged
 *  steps + task. */
async function updateTask(
  companyId: number,
  signalKey: number,
  patch: WorkbenchTaskPatch,
  token: string | null,
): Promise<WorkbenchTaskUpdate> {
  const { data } = await api.patch<WorkbenchTaskUpdate>(
    `/workbench/companies/${companyId}/signals/${signalKey}/`,
    patch,
    authConfig(token),
  );
  return data;
}

const API_URL = import.meta.env.VITE_API_URL as string;

/** POST /api/workbench/companies/<company_id>/signals/<signal_key>/draft/ — stream
 *  a MarcusAI draft over SSE. `stepIndex` null/undefined drafts the whole signal;
 *  a number drafts that one step. Resolves when the stream ends (done frame,
 *  socket close, or abort); rejects only on a transport-level failure. Content
 *  errors arrive as an `error` frame → `handlers.onError`.
 *
 *  Uses `fetch` (not axios) for incremental reads. Pass an AbortSignal to cancel. */
async function streamDraft(
  params: { companyId: number; signalKey: number; stepIndex?: number | null },
  token: string | null,
  workspaceId: string | number | null,
  handlers: DraftStreamHandlers,
  abortSignal?: AbortSignal,
): Promise<void> {
  const { companyId, signalKey, stepIndex } = params;
  const url = `${API_URL}/api/workbench/companies/${companyId}/signals/${signalKey}/draft/`;

  // NB: deliberately NOT sending `Accept: text/event-stream`. The endpoint is a
  // DRF APIView that returns a StreamingHttpResponse, but DRF content negotiation
  // runs first and only has the JSON renderer — a text/event-stream Accept header
  // makes it 406 ("Could not satisfy the request Accept header") before the
  // handler runs. fetch's default `Accept: */*` negotiates fine and the view
  // still streams SSE.
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers.Authorization = `JWT ${token}`;
  if (workspaceId != null) headers["X-Workspace-ID"] = String(workspaceId);

  const dispatch = (frame: DraftFrame) => {
    switch (frame.type) {
      case "meta":
        handlers.onMeta?.(frame);
        break;
      case "chunk":
        handlers.onChunk(frame.text);
        break;
      case "error":
        handlers.onError?.(frame.detail);
        break;
      case "done":
        handlers.onDone?.();
        break;
    }
  };

  // SSE frames are separated by a blank line; each frame's payload is the
  // concatenation of its `data:` lines.
  const flushFrame = (raw: string) => {
    const dataLines = raw
      .split("\n")
      .filter((l) => l.startsWith("data:"))
      .map((l) => l.slice(5).replace(/^ /, ""));
    if (dataLines.length === 0) return;
    try {
      dispatch(JSON.parse(dataLines.join("\n")) as DraftFrame);
    } catch {
      /* ignore an unparseable frame rather than aborting the whole stream */
    }
  };

  // The whole request lives in one try so an abort during the initial fetch
  // handshake (user switched fixes before the stream opened) is swallowed just
  // like a mid-stream abort, rather than escaping as an unhandled rejection.
  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(stepIndex != null ? { step_index: stepIndex } : {}),
      signal: abortSignal,
    });

    // A non-2xx before the stream opens (403/404/400) has a JSON body, not SSE.
    if (!res.ok || !res.body) {
      let detail = `Draft failed (${res.status}).`;
      try {
        const body = await res.json();
        detail = body?.detail || body?.error || detail;
      } catch {
        /* non-JSON error body — keep the status message */
      }
      handlers.onError?.(detail);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      // Handle both \n\n and \r\n\r\n frame delimiters.
      while ((sep = buffer.search(/\r?\n\r?\n/)) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + buffer.slice(sep).match(/^\r?\n\r?\n/)![0].length);
        flushFrame(frame);
      }
    }
    if (buffer.trim()) flushFrame(buffer);
  } catch (err) {
    // Aborts are expected (user navigated / re-drafted) — swallow them.
    if ((err as Error)?.name !== "AbortError") {
      handlers.onError?.((err as Error)?.message || "Draft stream interrupted.");
    }
  }
}

export const workbenchClient = {
  getQueue,
  updateTask,
  streamDraft,
};

export default workbenchClient;
